import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { tone, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";

export type ConfirmDialogProps = {
  title: string;

  message?: string | undefined;

  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;

  destructive?: boolean | undefined;
  tone?: Tone | undefined;

  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  tone: which,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const t = useT();
  const tokens = tone(which);
  const [pending, setPending] = useState(false);

  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const decline = useRef<HTMLButtonElement>(null);
  const accept = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const target = destructive ? decline.current : accept.current;
    target?.focus({ preventScroll: true });
  }, [destructive]);

  const dismiss = () => {
    if (pending) return;
    onCancel();
  };

  const confirm = () => {
    if (pending) return;
    const result = onConfirm();
    if (!(result instanceof Promise)) return;
    setPending(true);
    void result.finally(() => {
      if (alive.current) setPending(false);
    });
  };

  return (
    <Modal
      footer={
        <>
          <Button
            disabled={pending}
            onClick={dismiss}
            ref={decline}
            tone={which}
            variant="secondary"
          >
            {cancelLabel ?? t("common.cancel")}
          </Button>
          <Button
            loading={pending}
            onClick={confirm}
            ref={accept}
            tone={which}
            variant={destructive ? "danger" : "primary"}
          >
            {confirmLabel ?? t("common.confirm")}
          </Button>
        </>
      }
      onClose={dismiss}
      onStage={which === "stage"}
      size="sm"
      title={title}
    >
      {}
      {message === undefined ? null : <p className={cn("text-sm", tokens.muted)}>{message}</p>}
    </Modal>
  );
}
